import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Search, 
  MessageSquare, 
  MoreVertical, 
  Plus, 
  Smartphone, 
  Shield, 
  CheckCircle2, 
  X,
  PlusCircle,
  Loader2,
  UserPlus,
  RefreshCw,
  Tag,
  Link2,
  Trash2,
  Archive,
  CheckSquare,
  Square,
  FolderPlus,
  Edit2,
  ChevronDown,
  ExternalLink,
  Send,
  Pin
} from 'lucide-react';

import { useStore } from '../store/useStore';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
// import { SMSAnalysis, analyzeSMSConversation } from '../lib/sms-analysis-service';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { sendSMS } from '../lib/sms-service';

// --- Types ---
interface Conversation {
  phone: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  leadName?: string;
  leadId?: string;
  isHighIntent?: boolean;
}

interface ConvMeta {
  tags: string[];
  folderId: string | null;
  linkedLeadId: string | null;
  archived: boolean;
}

interface SmsFolder {
  id: string;
  name: string;
  color: string;
}

const LS_META = 'ws_sms_conv_meta';
const LS_FOLDERS = 'ws_sms_folders';
const LS_TAGS = 'ws_sms_tags';

const DEFAULT_TAGS = ['hot', 'cold', 'follow-up', 'interested', 'closed'];

const TAG_COLORS: Record<string, string> = {
  hot: '#ef4444',
  cold: '#3b82f6',
  'follow-up': '#f59e0b',
  interested: '#10b981',
  closed: '#6b7280',
  custom: '#8b5cf6',
};

function loadMeta(): Record<string, ConvMeta> {
  try { return JSON.parse(localStorage.getItem(LS_META) || '{}'); } catch { return {}; }
}
function saveMeta(m: Record<string, ConvMeta>) {
  localStorage.setItem(LS_META, JSON.stringify(m));
}
function loadFolders(): SmsFolder[] {
  try { return JSON.parse(localStorage.getItem(LS_FOLDERS) || '[]'); } catch { return []; }
}
function saveFolders(f: SmsFolder[]) {
  localStorage.setItem(LS_FOLDERS, JSON.stringify(f));
}


const CARRIER_OPTIONS = [
  'Auto-Detect (Universal Blast)',
  'verizon',
  'at&t',
  't-mobile',
  'sprint',
  'boost-mobile',
  'cricket-wireless',
  'metro-pcs',
  'u.s.-cellular',
  'virgin-mobile',
];

// --- Helpers ---
const formatPhoneNumber = (phone: string | null | undefined) => {
  if (!phone || typeof phone !== 'string') return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const normalize = (p: string | null | undefined) => {
  if (!p || typeof p !== 'string') return '';
  const raw = p.replace(/\D/g, '');
  return raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw;
};

const safeFormatDate = (dateVal: any, formatStr: string): string => {
  if (!dateVal) return '';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return format(d, formatStr);
  } catch (e) {
    return '';
  }
};

const stripSmsPrefix = (content: string | null | undefined) => {
  if (!content || typeof content !== 'string') return '';
  const prefixes = [
    /^\d+\/[^:]+:\s*/, // "2/Verizon: "
    /^[^:]+:\s*/,      // "Verizon: "
  ];
  let stripped = content;
  for (const p of prefixes) {
    stripped = stripped.replace(p, '');
  }
  return stripped;
};

// --- Components ---
const ConfirmModal = ({ 
  isOpen, 
  title, 
  message, 
  onConfirm, 
  onClose 
}: { 
  isOpen: boolean; 
  title: string; 
  message: string; 
  onConfirm: () => void; 
  onClose: () => void;
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
        <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--t-text)' }}>{title}</h3>
        <p className="text-sm mb-6" style={{ color: 'var(--t-text-muted)' }}>{message}</p>
        <div className="flex gap-3">
          <button 
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl font-bold transition-all"
            style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text)' }}
          >Cancel</button>
          <button 
            onClick={() => { onConfirm(); onClose(); }}
            className="flex-1 py-2.5 rounded-xl font-bold transition-all"
            style={{ background: 'var(--t-error)', color: '#fff' }}
          >Confirm</button>
        </div>
      </div>
    </div>
  );
};

export default function SMSInbox() {
  const { 
    currentUser, 
    leads, 
    addTimelineEntry,
    smsMessages,
    addSMSMessage,
    syncGoogleContacts,
    contacts: storeContacts,
    deleteSMSByPhone,
    deleteSMSByPhones,
    undo
  } = useStore();
  
  const navigate = useNavigate();

  // Local filtered messages
  const [_loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'inbox' | 'contacts' | string>('inbox');
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [newNumber, setNewNumber] = useState('');

  const [pinnedPhones, setPinnedPhones] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Provisioned Numbers (Twilio/BYO)
  const [provisionedNumbers, setProvisionedNumbers] = useState<string[]>([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState<string>('Gmail Gateway');

  // Conversation metadata (tags, folders, linked lead, archived)
  const [convMeta, setConvMeta] = useState<Record<string, ConvMeta>>(loadMeta);
  const [folders, setFolders] = useState<SmsFolder[]>(loadFolders);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Bulk selection
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  // Tag filter
  const [tagFilter, setTagFilter] = useState<string | null>(null);


  // Assign lead modal
  const [assignLeadModal, setAssignLeadModal] = useState<{ isOpen: boolean; phone: string }>({ isOpen: false, phone: '' });
  const [assignLeadSearch, setAssignLeadSearch] = useState('');

  // Tag picker open for which phone
  const [tagPickerPhone, setTagPickerPhone] = useState<string | null>(null);

  // Available tags state
  const [availableTags, setAvailableTags] = useState<string[]>(() => {
    try { 
      const saved = localStorage.getItem(LS_TAGS);
      return saved ? JSON.parse(saved) : DEFAULT_TAGS; 
    } catch { return DEFAULT_TAGS; }
  });

  const addCustomTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed || availableTags.includes(trimmed)) return;
    const updated = [...availableTags, trimmed];
    setAvailableTags(updated);
    localStorage.setItem(LS_TAGS, JSON.stringify(updated));
    toast.success(`Tag #${trimmed} created`);
  };

  const deleteTag = (tag: string) => {
    const updated = availableTags.filter(t => t !== tag);
    setAvailableTags(updated);
    localStorage.setItem(LS_TAGS, JSON.stringify(updated));
    // Clean up meta
    setConvMeta(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(p => {
        if (next[p].tags.includes(tag)) next[p] = { ...next[p], tags: next[p].tags.filter(t => t !== tag) };
      });
      saveMeta(next);
      return next;
    });
  };

  // Contacts / Leads matching — use store contacts, supplemented by local overrides
  const [localContacts, setLocalContacts] = useState<Array<{ name: string; phone: string; id: string; createdAt?: string }>>(() => {
    try {
      const saved = localStorage.getItem('ws_sms_contacts');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const contacts = useMemo(() => {
    const mergedMap = new Map<string, { name: string; phone: string; id: string; createdAt?: string }>();
    (storeContacts || []).forEach(c => {
      if (c?.phone) mergedMap.set(c.phone.replace(/\D/g, ''), c);
    });
    localContacts.forEach(c => {
      if (c?.phone) mergedMap.set(c.phone.replace(/\D/g, ''), c);
    });
    return Array.from(mergedMap.values());
  }, [storeContacts, localContacts]);
  const [editNameModal, setEditNameModal] = useState({ isOpen: false, currentName: '', phone: '' });
  const [editNameValue, setEditNameValue] = useState('');
  const [carrierMap, setCarrierMap] = useState<Record<string, string>>({});
  const [carrierPicker, setCarrierPicker] = useState({ isOpen: false, phone: '', message: '', selectedCarrier: 'Auto-Detect (Universal Blast)' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompose, setShowCompose] = useState(false);

  // Recovery / Confirm Modal
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // --- Meta helpers ---
  const getMeta = useCallback((phone: string): ConvMeta => {
    const p = (phone || '').replace(/\D/g, '');
    return convMeta[p] || { tags: [], folderId: null, linkedLeadId: null, archived: false };
  }, [convMeta]);

  const updateMeta = useCallback((phone: string, patch: Partial<ConvMeta>) => {
    const p = (phone || '').replace(/\D/g, '');
    setConvMeta(prev => {
      const updated = { ...prev, [p]: { ...(prev[p] || { tags: [], folderId: null, linkedLeadId: null, archived: false }), ...patch } };
      saveMeta(updated);
      return updated;
    });
  }, []);

  const toggleTag = useCallback((phone: string, tag: string) => {
    const meta = getMeta(phone);
    const tags = meta.tags.includes(tag) ? meta.tags.filter(t => t !== tag) : [...meta.tags, tag];
    updateMeta(phone, { tags });
  }, [getMeta, updateMeta]);

  const createFolder = () => {
    if (!newFolderName.trim()) return;
    const f: SmsFolder = { id: Date.now().toString(), name: newFolderName.trim(), color: '#8b5cf6' };
    const updated = [...folders, f];
    setFolders(updated);
    saveFolders(updated);
    setNewFolderName('');
    setShowNewFolderModal(false);
    toast.success(`Folder "${f.name}" created`);
  };

  const deleteFolder = (id: string) => {
    const updated = folders.filter(f => f.id !== id);
    setFolders(updated);
    saveFolders(updated);
    if (activeTab === id) setActiveTab('inbox');
    // Unassign all conversations in this folder
    setConvMeta(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(p => { if (next[p].folderId === id) next[p] = { ...next[p], folderId: null }; });
      saveMeta(next);
      return next;
    });
    toast.success('Folder deleted');
  };

  const renameFolder = (id: string, currentName: string) => {
    const name = prompt("Rename folder to:", currentName);
    if (name && name.trim()) {
      const updated = folders.map(f => f.id === id ? { ...f, name: name.trim() } : f);
      setFolders(updated);
      saveFolders(updated);
      toast.success('Folder renamed');
    }
  };



  // Selected conversation specific derived state
  const selectedMessages = useMemo(() => {
    if (!selectedPhone) return [];
    const p = selectedPhone.replace(/\D/g, '');
    return (smsMessages || [])
      .filter(m => m.phone_number && m.phone_number.replace(/\D/g, '') === p)
      .sort((a, b) => {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return timeA - timeB;
      });
  }, [smsMessages, selectedPhone]);

  // Load from local storage / init
  useEffect(() => {
    const savedCarrierMap = localStorage.getItem('ws_carrier_map');
    if (savedCarrierMap) setCarrierMap(JSON.parse(savedCarrierMap));

    const savedPinned = localStorage.getItem('ws_pinned_sms');
    if (savedPinned) setPinnedPhones(new Set(JSON.parse(savedPinned)));

    const startTime = Date.now();
    
    // Force hide after 1800ms regardless of data
    const maxTimer = setTimeout(() => {
      setLoading(false);
    }, 1800);

    const init = async () => {
      setLoading(true);
      try {
        await useStore.getState().syncSMSUnreadCount();
        await Promise.all([
          useStore.getState().fetchSMSMessages(),
          useStore.getState().fetchContacts()
        ]);
        
        // Fetch provisioned numbers from preferences
        const { data: prefs } = await supabase
          .from('agent_preferences')
          .select('twilio_phone_numbers')
          .eq('user_id', currentUser?.id)
          .single();
        
        if (prefs?.twilio_phone_numbers) {
          const nums = Array.isArray(prefs.twilio_phone_numbers) ? prefs.twilio_phone_numbers : [];
          setProvisionedNumbers(nums);
          if (nums.length > 0) setSelectedFromNumber(nums[0]);
        }

        const { pollSMSMessages } = await import('../lib/sms-polling');
        await pollSMSMessages();
        await useStore.getState().syncSMSUnreadCount();
      } catch (err) {
        console.error('Failed to init SMS:', err);
      } finally {
        const elapsed = Date.now() - startTime;
        if (elapsed < 300) {
          setTimeout(() => {
            clearTimeout(maxTimer);
            setLoading(false);
          }, 300 - elapsed);
        } else {
          clearTimeout(maxTimer);
          setLoading(false);
        }
      }
    };
    if (currentUser?.id) init();
  }, [currentUser]);

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ text: string, type: 'success' | 'error' | 'loading' } | null>(null);

  const handleSyncContacts = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMessage({ text: 'Syncing Google Contacts...', type: 'loading' });
    console.log('[SMS Sync] Syncing contacts...');
    try {
      const result = await syncGoogleContacts();
      console.log('[SMS Sync] Result:', result);
      
      const msg = result.count > 0 
        ? `Successfully synced ${result.count} new contacts`
        : 'All contacts are already up to date';
      
      setSyncMessage({ text: msg, type: 'success' });
      setTimeout(() => setSyncMessage(null), 4000);
    } catch (err: any) {
      console.error('[SMS Sync] Error:', err);
      const errorMsg = err.message || 'Failed to sync contacts';
      setSyncMessage({ text: errorMsg, type: 'error' });
      setTimeout(() => setSyncMessage(null), 4000);
    } finally {
      setSyncing(false);
    }
  };

  // Auto-scroll to bottom when conversation opens or new messages arrive
  useEffect(() => {
    if (!messagesEndRef.current) return;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: selectedPhone ? 'auto' : 'smooth' });
    });
  }, [smsMessages, selectedPhone]);

  // Mark currently open conversation messages as read when new messages arrive or conversation is selected
  useEffect(() => {
    if (selectedPhone) {
      const normalizedSel = normalize(selectedPhone);
      const hasUnread = (smsMessages || []).some(
        m => m.direction === 'inbound' && !m.is_read && normalize(m.phone_number) === normalizedSel
      );
      if (hasUnread) {
        markAsRead(selectedPhone);
      }
    }
  }, [selectedPhone, smsMessages]);

  // Aggregate messages into conversations
  const conversations = useMemo(() => {
    const map: Record<string, Conversation> = {};
    
    // First, populate all contacts as baseline conversations
    (contacts || []).forEach(contact => {
      const p = normalize(contact.phone);
      if (!p) return;
      map[p] = {
        phone: p,
        lastMessage: 'Tap to start conversation',
        timestamp: contact.createdAt || new Date().toISOString(),
        unreadCount: 0,
        leadName: contact.name || formatPhoneNumber(p),
        leadId: contact.id
      };
    });

    // Then overlay actual SMS messages, prioritizing their timestamp and content
    (smsMessages || []).forEach(msg => {
      const p = normalize(msg.phone_number);
      if (!p) return;
      const existing = map[p];
      
      const timeNew = msg.created_at ? new Date(msg.created_at).getTime() : 0;
      const timeExist = existing?.timestamp ? new Date(existing.timestamp).getTime() : 0;
      
      const isNewer = !existing || 
                      existing.lastMessage === 'Tap to start conversation' || 
                      timeNew > timeExist;
      
      const prevUnread = existing?.unreadCount || 0;
      const thisUnread = (msg.direction === 'inbound' && !msg.is_read) ? 1 : 0;
      
      if (isNewer) {
        const lead = leads.find(l => normalize(l.phone) === p);
        const contact = contacts.find(c => normalize(c.phone) === p);
        
        map[p] = {
          phone: p,
          lastMessage: msg.content || '',
          timestamp: msg.created_at || new Date().toISOString(),
          unreadCount: prevUnread + thisUnread,
          leadName: lead?.name || contact?.name || formatPhoneNumber(p),
          leadId: lead?.id
        };
      } else if (existing) {
        existing.unreadCount = prevUnread + thisUnread;
      }
    });

    return Object.values(map).sort((a, b) => {
      const aPinned = pinnedPhones.has(a.phone) ? 1 : 0;
      const bPinned = pinnedPhones.has(b.phone) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    });
  }, [smsMessages, leads, contacts, pinnedPhones]);

  const filteredConversations = conversations.filter(c => 
    c.phone.includes(searchQuery) || 
    (c.leadName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.lastMessage || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  const activeConversation = conversations.find(c => normalize(c.phone) === normalize(selectedPhone || ''));
  const displayName = activeConversation?.leadName || formatPhoneNumber(selectedPhone);

  const markAsRead = (phone: string) => {
    const { markSMSAsRead } = useStore.getState();
    markSMSAsRead(phone);
  };
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedPhone) return;
    const rawPhone = normalize(selectedPhone);
    const carrier = carrierMap[rawPhone] || 'Auto-Detect (Universal Blast)';
    executeSend(rawPhone, replyText, carrier === 'Auto-Detect (Universal Blast)' ? undefined : carrier);
  };

  const executeSend = async (phone: string, text: string, carrier?: string) => {
    setSending(true);
    const tempId = Math.random().toString(36).substring(7);
    const optimisticMsg: any = {
      id: tempId,
      content: text,
      user_id: currentUser?.id || '',
      phone_number: phone,
      direction: 'outbound',
      created_at: new Date().toISOString(),
      is_read: true,
      status: 'sending'
    };
    
    addSMSMessage(optimisticMsg);
    setReplyText('');

    try {
      const fromNumber = selectedFromNumber === 'Gmail Gateway' ? undefined : selectedFromNumber;
      const res = await sendSMS(phone, text, {
        carrier,
        userId: currentUser?.id,
        fromNumber
      });

      if (!res.success) {
        toast.error(`Delivery failed: ${res.message}`);
        return;
      }

      // Update timeline if it's a lead
      const lead = leads.find(l => normalize(l.phone) === phone);
      if (lead) {
        addTimelineEntry(lead.id, {
          type: 'sms',
          content: `SMS Sent (${fromNumber || 'Gateway'}): ${text}`,
          timestamp: new Date().toISOString(),
          user: currentUser?.name || 'System'
        });
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const saveCarrier = (phone: string, carrier: string) => {
    const newMap = { ...carrierMap, [phone]: carrier };
    setCarrierMap(newMap);
    localStorage.setItem('ws_carrier_map', JSON.stringify(newMap));
  };

  const togglePin = (phone: string) => {
    const newPinned = new Set(pinnedPhones);
    if (newPinned.has(phone)) newPinned.delete(phone);
    else newPinned.add(phone);
    setPinnedPhones(newPinned);
    localStorage.setItem('ws_pinned_sms', JSON.stringify(Array.from(newPinned)));
  };

  const addContact = (contact: { name: string; phone: string }) => {
    const id = Math.random().toString(36).substring(7);
    const updated = [...localContacts.filter(c => normalize(c.phone) !== normalize(contact.phone)), { ...contact, id }];
    setLocalContacts(updated);
    localStorage.setItem('ws_sms_contacts', JSON.stringify(updated));
    toast.success('Contact saved');
  };



  // Bulk action helpers
  const toggleSelectPhone = (phone: string) => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  };
  const bulkArchive = () => {
    selectedPhones.forEach(p => updateMeta(p, { archived: true }));
    setSelectedPhones(new Set());
    setBulkMode(false);
    toast.success(`Archived ${selectedPhones.size} conversations`);
  };
  const bulkDelete = async () => {
    const phonesToDelete = Array.from(selectedPhones);
    setSelectedPhones(new Set());
    setBulkMode(false);
    const res = await deleteSMSByPhones(phonesToDelete);
    if (res.success) {
      toast((t) => (
        <div className="flex items-center justify-between gap-4 w-full">
          <span>Deleted {phonesToDelete.length} conversations.</span>
          <button
            onClick={() => {
              undo();
              toast.dismiss(t.id);
              toast.success('Action undone!');
            }}
            className="px-2.5 py-1 rounded bg-[var(--t-primary)] text-white text-[10px] font-black uppercase tracking-wider hover:scale-105 active:scale-95 transition-all"
          >
            Undo
          </button>
        </div>
      ), {
        duration: 5000,
        position: 'bottom-right',
        style: {
          background: 'var(--t-surface)',
          color: 'var(--t-text)',
          border: '1px solid var(--t-border)',
          boxShadow: 'var(--t-glow-shadow)'
        }
      });
    } else {
      toast.error(res.error || 'Failed to delete conversations');
    }
  };
  const bulkTag = (tag: string) => {
    selectedPhones.forEach(p => {
      const meta = getMeta(p);
      if (!meta.tags.includes(tag)) updateMeta(p, { tags: [...meta.tags, tag] });
    });
    setSelectedPhones(new Set());
    setBulkMode(false);
    toast.success(`Tagged ${selectedPhones.size} conversations as "${tag}"`);
  };

  // Conversations that actually have SMS messages, filtered by tab/tag/folder
  const smsOnlyConversations = useMemo(() => {
    const numbersWithMsgs = new Set((smsMessages || []).map(m => normalize(m.phone_number)));
    
    return filteredConversations.filter(c => {
      const p = normalize(c.phone);
      if (!numbersWithMsgs.has(p)) return false;
      const meta = getMeta(p);
      if (meta.archived) return false;
      // Tab filter
      if (activeTab === 'inbox') {
        if (meta.folderId !== null) return false; // in a folder, not inbox
      } else if (activeTab !== 'contacts') {
        if (meta.folderId !== activeTab) return false; // custom folder tab
      }
      // Tag filter
      if (tagFilter && !meta.tags.includes(tagFilter)) return false;
      return true;
    });
  }, [filteredConversations, smsMessages, convMeta, activeTab, tagFilter, getMeta]);

  return (
    <div className="flex h-full overflow-hidden relative" style={{ background: 'var(--t-bg)' }}>
      {/* Sync Message Overlay Fallback */}
      {syncMessage && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] animate-in fade-in slide-in-from-top-2 duration-300">
          <div className={`px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-xl ${
            syncMessage.type === 'success' ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 
            syncMessage.type === 'error' ? 'bg-rose-500/20 border-rose-500/30 text-rose-400' :
            'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
          }`} style={{ background: 
            syncMessage.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 
            syncMessage.type === 'error' ? 'rgba(244, 63, 94, 0.15)' :
            'rgba(99, 102, 241, 0.15)' 
          }}>
            {syncMessage.type === 'success' ? <CheckCircle2 size={18} /> : 
             syncMessage.type === 'error' ? <X size={18} /> :
             <RefreshCw size={18} className="animate-spin" />}
            <span className="text-xs font-black uppercase tracking-widest">{syncMessage.text}</span>
          </div>
        </div>
      )}

      {/* Sidebar - Conversation List */}
      <div className={`w-full md:w-80 flex flex-col border-r transition-all ${selectedPhone ? 'hidden md:flex' : 'flex'}`} style={{ borderColor: 'var(--t-border)', background: 'rgba(var(--t-surface-rgb), 0.4)' }}>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold uppercase tracking-widest pl-2" style={{ color: 'var(--t-text)' }}>SMS</h1>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleSyncContacts}
                disabled={syncing}
                className="p-2 rounded-xl transition-all hover:scale-105 active:scale-95 border"
                style={{ borderColor: 'var(--t-border)', color: syncing ? 'var(--t-primary)' : 'var(--t-text-muted)', background: 'var(--t-surface)' }}
                title="Sync Google Contacts"
              >
                <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
              </button>
              <button 
                onClick={() => setShowCompose(true)}
                className="p-2 rounded-xl transition-all hover:scale-105 active:scale-95"
                style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}
              >
                <Plus size={20} />
              </button>
            </div>
          </div>
          {/* Tab Scrollable Row: Inbox | Contacts | Custom Folders */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar pb-0.5">
            {(['inbox', 'contacts'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all"
                style={activeTab === tab ? { background: 'var(--t-primary)', color: 'var(--t-on-primary)' } : { background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
              >{tab}</button>
            ))}
            {folders.map(f => (
              <div key={f.id} className="relative group/folder shrink-0">
                <button onClick={() => setActiveTab(f.id)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-wider transition-all pr-8"
                  style={activeTab === f.id ? { background: f.color, color: '#fff' } : { background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
                >{f.name}</button>
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center opacity-0 group-hover/folder:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); renameFolder(f.id, f.name); }} className="p-1 hover:text-white"><Edit2 size={10} /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }} className="p-1 hover:text-white"><Trash2 size={10} /></button>
                </div>
              </div>
            ))}
            <button onClick={() => setShowNewFolderModal(true)}
              className="shrink-0 p-1.5 rounded-lg transition-all"
              style={{ background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
              title="New folder"
            ><FolderPlus size={14} /></button>
          </div>
          {/* Tag filter bar */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            <button onClick={() => setTagFilter(null)}
              className="shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all"
              style={!tagFilter ? { background: 'var(--t-primary)', color: 'var(--t-on-primary)' } : { background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
            >All</button>
            {availableTags.map(tag => (
              <div key={tag} className="relative group/tag shrink-0">
                <button onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all"
                  style={tagFilter === tag ? { background: TAG_COLORS[tag] || '#8b5cf6', color: '#fff' } : { background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
                >#{tag}</button>
                {tagFilter === tag && (
                  <button onClick={(e) => { e.stopPropagation(); deleteTag(tag); setTagFilter(null); }} className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 text-white flex items-center justify-center text-[8px] opacity-0 group-hover/tag:opacity-100 transition-opacity"><X size={8} /></button>
                )}
              </div>
            ))}
            <button onClick={() => { const t = prompt("New tag name:"); if(t) addCustomTag(t); }}
              className="shrink-0 p-1 rounded-lg transition-all"
              style={{ background: 'var(--t-surface)', color: 'var(--t-text-muted)' }}
              title="New tag"
            ><PlusCircle size={14} /></button>
          </div>
          {/* Bulk action toolbar */}
          <div className="flex items-center gap-1.5">
            <button onClick={() => { setBulkMode(!bulkMode); setSelectedPhones(new Set()); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border"
              style={bulkMode ? { background: 'var(--t-primary)', color: 'var(--t-on-primary)', borderColor: 'var(--t-primary)' } : { background: 'var(--t-surface)', color: 'var(--t-text-muted)', borderColor: 'var(--t-border)' }}
            ><CheckSquare size={12} /> Select</button>
            {bulkMode && selectedPhones.size > 0 && (<>
              <button onClick={bulkArchive} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-bold transition-all" style={{ background: 'var(--t-surface)', color: 'var(--t-text-muted)', border: '1px solid var(--t-border)' }}>
                <Archive size={12} /> Archive
              </button>
              <button onClick={bulkDelete} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-bold transition-all" style={{ background: 'var(--t-error)', color: '#fff' }}>
                <Trash2 size={12} /> Delete
              </button>
              <div className="relative group/bulktag">
                <button className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-bold transition-all" style={{ background: 'var(--t-surface)', color: 'var(--t-text-muted)', border: '1px solid var(--t-border)' }}>
                  <Tag size={12} /> Tag <ChevronDown size={10} />
                </button>
                <div className="absolute top-7 left-0 z-50 hidden group-hover/bulktag:flex flex-col gap-0.5 p-1 rounded-xl shadow-xl min-w-[100px]" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                  {availableTags.map((t: string) => <button key={t} onClick={() => bulkTag(t)} className="px-3 py-1.5 text-[11px] text-left rounded-lg hover:bg-[var(--t-surface-hover)] font-medium" style={{ color: TAG_COLORS[t] || 'var(--t-text)' }}>#{t}</button>)}
                </div>
              </div>
            </>)}
            {bulkMode && <span className="text-[10px] ml-auto" style={{ color: 'var(--t-text-muted)' }}>{selectedPhones.size} selected</span>}
          </div>
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ backgroundColor: 'var(--t-surface)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-1 p-2 custom-scrollbar">
          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-1">
              {contacts.length === 0 ? (
                <div className="p-6 text-center" style={{ color: 'var(--t-text-muted)' }}>
                  <UserPlus className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium mb-1">No contacts yet</p>
                  <p className="text-xs">Click the sync button to import Google Contacts</p>
                </div>
              ) : contacts.filter(c =>
                !searchQuery ||
                (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                c.phone.includes(searchQuery)
              ).map(c => (
                <button
                  key={c.id || c.phone}
                  onClick={() => { setSelectedPhone(c.phone); markAsRead(c.phone); setActiveTab('inbox'); }}
                  className="w-full p-3 rounded-2xl text-left transition-all hover:bg-[var(--t-surface-hover)] flex items-center gap-3 border border-transparent"
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)' }}>
                    {(c.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--t-text)' }}>{c.name || formatPhoneNumber(c.phone)}</p>
                    <p className="text-[10px] font-mono" style={{ color: 'var(--t-text-muted)' }}>{formatPhoneNumber(c.phone)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Inbox / Folder Tabs list */}
          {activeTab !== 'contacts' && (
            _loading ? (
              <div className="flex flex-col gap-2 p-3 relative h-full overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center z-[10] pointer-events-none">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-5 h-5 text-[var(--t-primary)] animate-spin" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-primary)] animate-pulse">Loading conversations...</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 opacity-30">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="p-4 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-border)] animate-skeleton">
                      <div className="flex justify-between mb-2">
                        <div className="h-4 w-24 bg-[var(--t-border)] rounded" />
                        <div className="h-2 w-10 bg-[var(--t-border)] rounded opacity-50" />
                      </div>
                      <div className="h-2.5 w-full bg-[var(--t-border)] rounded opacity-40" />
                    </div>
                  ))}
                </div>
              </div>
            ) : smsOnlyConversations.map((conv) => {
            const meta = getMeta(conv.phone);
            const isSelected = normalize(selectedPhone || '') === conv.phone;
            const isChecked = selectedPhones.has(conv.phone);
            return (
              <div key={conv.phone} className={`w-full rounded-2xl transition-all relative border group/conv ${
                isSelected ? 'border-[var(--t-primary)]/50' : 'border-transparent hover:bg-[var(--t-surface-hover)]'
              }`}
              style={isSelected ? { backgroundColor: 'var(--t-primary-dim)' } : {}}
              >
                <div className="flex items-start gap-2 p-3">
                  {bulkMode && (
                    <button onClick={() => toggleSelectPhone(conv.phone)} className="mt-1 shrink-0">
                      {isChecked ? <CheckSquare size={16} style={{ color: 'var(--t-primary)' }} /> : <Square size={16} style={{ color: 'var(--t-text-muted)' }} />}
                    </button>
                  )}
                  <button className="flex-1 text-left min-w-0" onClick={() => { setSelectedPhone(conv.phone); markAsRead(conv.phone); }}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-sm truncate max-w-[130px]" style={{ color: isSelected ? 'var(--t-primary)' : 'var(--t-text)' }}>{conv.leadName}</span>
                      <span className="text-[10px] opacity-60 shrink-0" style={{ color: 'var(--t-text-muted)' }}>{safeFormatDate(conv.timestamp, 'h:mm a')}</span>
                    </div>
                    <p className="text-[11px] line-clamp-1 leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>{stripSmsPrefix(conv.lastMessage)}</p>
                    {/* Tag badges */}
                    {meta.tags.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-1.5">
                        {meta.tags.map(t => (
                          <span key={t} className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: (TAG_COLORS[t] || '#8b5cf6') + '22', color: TAG_COLORS[t] || '#8b5cf6' }}>#{t}</span>
                        ))}
                      </div>
                    )}
                  </button>
                  {/* Quick Pin button */}
                  <div className="shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePin(conv.phone);
                      }}
                      className={`p-1 rounded-lg transition-all ${
                        pinnedPhones.has(conv.phone) 
                          ? 'opacity-100 text-[var(--t-warning)]' 
                          : 'opacity-0 group-hover/conv:opacity-60 hover:!opacity-100 text-[var(--t-text-muted)]'
                      }`}
                      title={pinnedPhones.has(conv.phone) ? 'Unpin conversation' : 'Pin conversation'}
                    >
                      <Pin size={12} className={pinnedPhones.has(conv.phone) ? 'fill-[var(--t-warning)] text-[var(--t-warning)]' : ''} />
                    </button>
                  </div>

                  {/* Quick tag button */}
                  <div className="relative shrink-0">
                    <button
                      onClick={() => setTagPickerPhone(tagPickerPhone === conv.phone ? null : conv.phone)}
                      className="p-1 rounded-lg opacity-0 group-hover/conv:opacity-60 hover:!opacity-100 transition-all"
                      style={{ color: 'var(--t-text-muted)' }}
                    ><Tag size={12} /></button>
                    {tagPickerPhone === conv.phone && (
                      <div className="absolute right-0 top-6 z-50 p-1.5 rounded-xl shadow-xl flex flex-col gap-0.5 min-w-[110px]" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                        {availableTags.map((t: string) => (
                          <button key={t} onClick={() => { toggleTag(conv.phone, t); }}
                            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium hover:bg-[var(--t-surface-hover)] text-left"
                            style={{ color: TAG_COLORS[t] || 'var(--t-text)' }}
                          >
                            <span className="w-2 h-2 rounded-full" style={{ background: TAG_COLORS[t] }} />
                            #{t}
                            {meta.tags.includes(t) && <CheckCircle2 size={10} className="ml-auto" />}
                          </button>
                        ))}
                        {/* Move to folder */}
                        {folders.length > 0 && <>
                          <div className="my-1 border-t" style={{ borderColor: 'var(--t-border)' }} />
                          {folders.map(f => (
                            <button key={f.id} onClick={() => { updateMeta(conv.phone, { folderId: meta.folderId === f.id ? null : f.id }); setTagPickerPhone(null); }}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium hover:bg-[var(--t-surface-hover)] text-left"
                              style={{ color: 'var(--t-text-muted)' }}
                            >📁 {f.name}{meta.folderId === f.id && ' ✓'}</button>
                          ))}
                        </>}
                      </div>
                    )}
                  </div>
                  {conv.unreadCount > 0 && (
                    <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-1 animate-pulse" style={{ background: 'var(--t-primary)' }} />
                  )}
                </div>
              </div>
            );
          }))}
          {activeTab === 'inbox' && smsOnlyConversations.length === 0 && (
            <div className="p-4 text-center" style={{ color: 'var(--t-text-muted)' }}>
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm mb-1">No conversations yet</p>
              <p className="text-xs">Compose a new message to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Chat Thread */}
      <div className={`flex-1 min-h-0 flex flex-col ${!selectedPhone ? 'hidden md:flex items-center justify-center' : 'flex'}`} style={{ backgroundColor: 'rgba(var(--t-surface-rgb), 0.6)' }}>
        {!selectedPhone ? (
          <div className="text-center p-8 max-w-sm">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'var(--t-surface)' }}>
              <Smartphone className="w-10 h-10" style={{ color: 'var(--t-text-muted)' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text)' }}>Select a conversation</h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
              Choose a contact from the left to view your message history and send SMS replies.
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Thread Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: 'rgba(var(--t-surface-rgb), 0.8)', borderColor: 'var(--t-border)' }}>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedPhone(null)}
                  className="md:hidden p-2 -ml-2 rounded-full hover:bg-[var(--t-surface-hover)]"
                >
                  <X size={20} />
                </button>
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-lg" style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)' }}>
                  {displayName?.charAt(0) || '?'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-bold text-base leading-tight" style={{ color: 'var(--t-text)' }}>{displayName}</h2>
                    {/* Linked lead badge */}
                    {(() => {
                      const meta = getMeta(selectedPhone || '');
                      const linked = leads.find(l => l.id === (meta.linkedLeadId || activeConversation?.leadId));
                      return linked ? (
                        <button onClick={() => navigate(`/leads?id=${linked.id}`)} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)' }}>
                          <ExternalLink size={9} /> {linked.name}
                        </button>
                      ) : (
                        <button onClick={() => setAssignLeadModal({ isOpen: true, phone: selectedPhone || '' })} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold opacity-50 hover:opacity-100 transition-all" style={{ background: 'var(--t-surface)', color: 'var(--t-text-muted)', border: '1px solid var(--t-border)' }}>
                          <Link2 size={9} /> Link Lead
                        </button>
                      );
                    })()}
                  </div>
                  <p className="text-[10px] font-mono opacity-60" style={{ color: 'var(--t-text-muted)' }}>{formatPhoneNumber(selectedPhone)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 relative">
                <button 
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`p-2 rounded-xl transition-all ${menuOpen ? 'bg-[var(--t-surface-hover)]' : 'hover:bg-[var(--t-surface-hover)]'}`} 
                  style={{ color: 'var(--t-text)' }}
                >
                  <MoreVertical size={20} />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-12 w-56 rounded-2xl shadow-2xl border p-2 z-[100] animate-in slide-in-from-top-2 duration-200" style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)' }}>
                    {/* Rename option */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--t-surface-hover)] transition-colors rounded-xl font-medium"
                      style={{ color: 'var(--t-text)' }}
                      onClick={() => {
                        setMenuOpen(false);
                        setEditNameValue(displayName || '');
                        setEditNameModal({ isOpen: true, currentName: displayName || '', phone: selectedPhone });
                      }}
                    >
                      🏷️ Rename Contact
                    </button>
                    {!activeConversation?.leadId && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--t-surface-hover)] transition-colors rounded-xl font-medium"
                        style={{ color: 'var(--t-primary)' }}
                        onClick={() => {
                          setMenuOpen(false);
                          const name = prompt("Enter contact name:");
                          if (name && selectedPhone) addContact({ name, phone: selectedPhone });
                        }}
                      >
                        <UserPlus size={14} /> Save Contact
                      </button>
                    )}
                    <div className="my-1 border-t" style={{ borderColor: 'var(--t-border)' }} />
                    <button
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--t-surface-hover)] transition-colors rounded-xl font-medium"
                      style={{ color: 'var(--t-text)' }}
                      onClick={() => {
                        setMenuOpen(false);
                        togglePin(selectedPhone?.replace(/\D/g, '') || '');
                      }}
                    >
                      {pinnedPhones.has(selectedPhone?.replace(/\D/g, '') || '') ? '📍 Unpin from Top' : '📌 Pin to Top'}
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--t-surface-hover)] transition-colors rounded-xl font-medium"
                      style={{ color: 'var(--t-text)' }}
                      onClick={() => {
                        setMenuOpen(false);
                        const rawPhone = selectedPhone?.replace(/\D/g, '') || '';
                        setCarrierPicker({
                          isOpen: true,
                          phone: rawPhone,
                          message: '',
                          selectedCarrier: carrierMap[rawPhone] || 'Auto-Detect (Universal Blast)'
                        });
                      }}
                    >
                      📡 Set Carrier ({carrierMap[selectedPhone?.replace(/\D/g, '') || ''] || 'Unknown'})
                    </button>
                    <div className="my-1 border-t" style={{ borderColor: 'var(--t-border)' }} />
                    <button
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--t-surface-hover)] transition-colors rounded-xl font-medium"
                      style={{ color: 'var(--t-error)' }}
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmModal({
                          isOpen: true,
                          title: 'Delete Conversation?',
                          message: `Are you sure you want to delete all messages with ${displayName}? This cannot be undone.`,
                          onConfirm: async () => {
                            if (!selectedPhone) return;
                            const phoneToDelete = selectedPhone;
                            setSelectedPhone(null);
                            const res = await deleteSMSByPhone(phoneToDelete);
                            if (res.success) {
                              toast((t) => (
                                <div className="flex items-center justify-between gap-4 w-full">
                                  <span>Conversation deleted.</span>
                                  <button
                                    onClick={() => {
                                      undo();
                                      toast.dismiss(t.id);
                                      toast.success('Action undone!');
                                    }}
                                    className="px-2.5 py-1 rounded bg-[var(--t-primary)] text-white text-[10px] font-black uppercase tracking-wider hover:scale-105 active:scale-95 transition-all"
                                  >
                                    Undo
                                  </button>
                                </div>
                              ), {
                                duration: 5000,
                                position: 'bottom-right',
                                style: {
                                  background: 'var(--t-surface)',
                                  color: 'var(--t-text)',
                                  border: '1px solid var(--t-border)',
                                  boxShadow: 'var(--t-glow-shadow)'
                                }
                              });
                            } else {
                              toast.error(res.error || 'Failed to delete conversation');
                            }
                          }
                        });
                      }}
                    >
                      🗑️ Delete Chat
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary Banner (Deactivated) */}

            {/* Messages Panel - fills remaining height, scrolls internally */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-6 scrollbar-hide relative">
              {_loading && (
                <div className="absolute inset-0 bg-[var(--t-bg)] z-[50] p-6 space-y-6">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] h-14 w-56 rounded-2xl animate-skeleton ${i % 2 === 0 ? 'bg-[var(--t-primary-dim)]' : 'bg-[var(--t-surface)] border border-[var(--t-border)]'}`} />
                    </div>
                  ))}
                </div>
              )}
              {selectedMessages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-30">
                  <MessageSquare size={48} className="mb-4" />
                  <p className="text-sm font-medium">No messages yet. Send a text to start the conversation.</p>
                </div>
              ) : (
                selectedMessages.map((msg, idx) => {
                  const showDate = idx === 0 || 
                    safeFormatDate(msg.created_at, 'yyyy-MM-dd') !== safeFormatDate(selectedMessages[idx-1].created_at, 'yyyy-MM-dd');

                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <div className="flex justify-center my-6">
                          <span className="text-[10px] uppercase tracking-widest font-bold px-4 py-1.5 rounded-full" style={{ backgroundColor: 'var(--t-surface)', border: '1px solid var(--t-border)', color: 'var(--t-text-muted)' }}>
                            {safeFormatDate(msg.created_at, 'MMMM dd, yyyy')}
                          </span>
                        </div>
                      )}
                      <div className={`flex w-full ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 shadow-sm ${
                          msg.direction === 'outbound' 
                            ? 'rounded-tr-none' 
                            : 'rounded-tl-none'
                        }`}
                        style={msg.direction === 'outbound' 
                          ? { background: 'var(--t-primary)', color: 'var(--t-on-primary)' } 
                          : { backgroundColor: 'var(--t-surface)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }
                        }
                        >
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{stripSmsPrefix(msg.content)}</p>
                          <div className={`flex items-center gap-1.5 mt-2 opacity-60 ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                            <span className="text-[9px] font-bold">
                              {safeFormatDate(msg.created_at, 'h:mm a')}
                            </span>
                            {msg.direction === 'outbound' && <CheckCircle2 size={10} />}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Area */}
            <div className="p-4 border-t bg-[var(--t-surface)]/40 backdrop-blur-xl" style={{ borderColor: 'var(--t-border)' }}>
              
              {/* AI Suggested Replies (Deactivated) */}

              {/* Quick Replies removed (Fix 7) */}

              <form onSubmit={handleSend} className="space-y-3">
                {/* Number Selector / Sender Context */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2 p-1 rounded-xl bg-[var(--t-background)] border border-[var(--t-border)]">
                    <div className="pl-3 py-1.5 flex items-center gap-2 border-r border-[var(--t-border)] pr-3">
                      <Shield className="w-3.5 h-3.5 text-[var(--t-primary)]" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">From:</span>
                    </div>
                    <select
                      value={selectedFromNumber}
                      onChange={(e) => setSelectedFromNumber(e.target.value)}
                      className="flex-1 bg-transparent border-none outline-none text-xs font-bold text-white py-1.5 cursor-pointer"
                    >
                      <option value="Gmail Gateway">Free Gmail Gateway</option>
                      {provisionedNumbers.map(num => (
                        <option key={num} value={num}>{num} (Twilio)</option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedPhone && carrierMap[selectedPhone.replace(/\D/g, '')] && (
                     <div className="px-3 py-2 rounded-xl bg-[var(--t-primary-dim)] border border-[var(--t-primary-dim)] flex items-center gap-2">
                       <CheckCircle2 className="w-3.5 h-3.5 text-[var(--t-primary)]" />
                       <span className="text-[10px] font-bold text-[var(--t-primary)] uppercase tracking-tighter">
                         {carrierMap[selectedPhone.replace(/\D/g, '')]} Verified
                       </span>
                     </div>
                  )}
                </div>

                <div className="relative group">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={selectedPhone ? `Message ${displayName || formatPhoneNumber(selectedPhone)}...` : 'Select a conversation to reply'}
                    disabled={!selectedPhone}
                    className="w-full px-4 py-3 pb-12 rounded-2xl text-sm outline-none transition-all resize-none min-h-[100px]"
                    style={{ 
                      backgroundColor: 'var(--t-background)', 
                      border: '1px solid var(--t-border)', 
                      color: 'var(--t-text)',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                  />
                  <div className="absolute right-3 bottom-3 flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={!replyText.trim() || sending || !selectedPhone}
                      className="SMSInbox-send-button flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:grayscale hover:scale-[1.02] active:scale-95 shadow-xl group/btn overflow-hidden relative"
                      style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary, #fff)' }}
                    >
                      <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-300" />
                      {sending ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center px-1">
                  <p className="text-[10px] text-[var(--t-text-muted)] italic">Press Enter to send, Shift+Enter for new line</p>
                  <p className="text-[10px] text-[var(--t-text-muted)]">{replyText.length} / 160 characters</p>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto custom-scrollbar" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold uppercase tracking-widest" style={{ color: 'var(--t-text)' }}>New Message</h3>
              <button onClick={() => setShowCompose(false)} className="p-2 rounded-full hover:bg-[var(--t-surface-hover)]" style={{ color: 'var(--t-text-muted)' }}><X size={20} /></button>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-60 ml-1" style={{ color: 'var(--t-text)' }}>Phone Number</label>
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                  <input 
                    type="tel"
                    value={newNumber}
                    onChange={(e) => setNewNumber(e.target.value)}
                    placeholder="Enter recipient..."
                    className="w-full pl-12 pr-4 py-4 rounded-2xl text-sm outline-none transition-all"
                    style={{ backgroundColor: 'var(--t-background)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-60 ml-1" style={{ color: 'var(--t-text)' }}>Quick Select</label>
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                  {[...leads, ...contacts].slice(0, 5).map((person, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setNewNumber(person?.phone || '');
                      }}
                      className="w-full flex items-center justify-between p-3 rounded-xl transition-all hover:bg-[var(--t-surface-hover)] border border-transparent hover:border-[var(--t-border)]"
                      style={{ backgroundColor: 'var(--t-background)' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[var(--t-primary-dim)] flex items-center justify-center font-bold text-[10px]" style={{ color: 'var(--t-primary)' }}>
                          {person?.name?.charAt(0) || ''}
                        </div>
                        <div className="text-left">
                          <p className="text-xs font-bold" style={{ color: 'var(--t-text)' }}>{person?.name || ''}</p>
                          <p className="text-[10px] opacity-60" style={{ color: 'var(--t-text-muted)' }}>{formatPhoneNumber(person?.phone)}</p>
                        </div>
                      </div>
                      <PlusCircle size={14} className="text-[var(--t-primary)] opacity-40" />
                    </button>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => {
                  if (newNumber.trim()) {
                    setSelectedPhone(newNumber.trim());
                    setShowCompose(false);
                    setNewNumber('');
                  }
                }}
                disabled={!newNumber.trim()}
                className="w-full py-4 rounded-2xl font-bold uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 shadow-lg shadow-[var(--t-primary)]/20"
                style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}
              >
                Send SMS
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Name Modal */}
      {editNameModal.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
            <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--t-text)' }}>Rename Contact</h3>
            <p className="text-xs mb-4 opacity-60" style={{ color: 'var(--t-text-muted)' }}>Updating for {formatPhoneNumber(editNameModal.phone)}</p>
            <input
              autoFocus
              type="text"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none mb-6"
              style={{ backgroundColor: 'var(--t-background)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setEditNameModal({ isOpen: false, currentName: '', phone: '' })}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text-muted)' }}
              >Cancel</button>
              <button
                onClick={() => {
                  if (editNameValue.trim() && editNameModal.phone) {
                    addContact({ name: editNameValue.trim(), phone: editNameModal.phone });
                    setEditNameModal({ isOpen: false, currentName: '', phone: '' });
                  }
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}
              >Save Name</button>
            </div>
          </div>
        </div>
      )}

      {/* Carrier Picker Modal */}
      {carrierPicker.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl animate-in zoom-in-95 duration-300" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-3xl bg-[var(--t-primary-dim)] flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-[var(--t-primary)]" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text)' }}>Carrier Verification</h3>
              <p className="text-xs leading-relaxed opacity-60" style={{ color: 'var(--t-text-muted)' }}>
                To reach {formatPhoneNumber(carrierPicker.phone)}, please select their wireless provider. This ensures delivery through the proper gateway.
              </p>
            </div>
            
            <div className="space-y-6">
              <select
                value={carrierPicker.selectedCarrier}
                onChange={(e) => setCarrierPicker(prev => ({ ...prev, selectedCarrier: e.target.value }))}
                className="w-full px-4 py-4 rounded-2xl text-sm outline-none appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--t-background)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
              >
                {CARRIER_OPTIONS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <div className="flex gap-3">
                <button
                  onClick={() => setCarrierPicker({ isOpen: false, phone: '', message: '', selectedCarrier: 'Auto-Detect (Universal Blast)' })}
                  className="flex-1 py-4 rounded-2xl text-sm font-bold"
                  style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text-muted)' }}
                >Cancel</button>
                <button
                  onClick={() => {
                    const { phone, message, selectedCarrier } = carrierPicker;
                    saveCarrier(phone, selectedCarrier);
                    setCarrierPicker({ isOpen: false, phone: '', message: '', selectedCarrier: 'Auto-Detect (Universal Blast)' });
                    if (message) {
                      executeSend(phone, message, selectedCarrier === 'Auto-Detect (Universal Blast)' ? undefined : selectedCarrier);
                    }
                  }}
                  className="flex-1 py-4 rounded-2xl text-sm font-bold shadow-lg shadow-[var(--t-primary)]/20"
                  style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}
                >Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Assign Lead Modal */}
      {assignLeadModal.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'var(--t-text)' }}>Link to Lead</h3>
              <button onClick={() => setAssignLeadModal({ isOpen: false, phone: '' })} className="p-1.5 rounded-xl hover:bg-[var(--t-surface-hover)]" style={{ color: 'var(--t-text-muted)' }}><X size={18} /></button>
            </div>
            <input
              autoFocus
              type="text"
              placeholder="Search leads..."
              value={assignLeadSearch}
              onChange={e => setAssignLeadSearch(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-sm outline-none mb-3"
              style={{ background: 'var(--t-background)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
            />
            <div className="max-h-64 overflow-y-auto space-y-1 custom-scrollbar">
              {leads
                .filter(l => !assignLeadSearch || l.name.toLowerCase().includes(assignLeadSearch.toLowerCase()) || (l.phone || '').includes(assignLeadSearch))
                .slice(0, 20)
                .map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => {
                      updateMeta(assignLeadModal.phone, { linkedLeadId: lead.id });
                      toast.success(`Linked to ${lead.name}`);
                      setAssignLeadModal({ isOpen: false, phone: '' });
                      setAssignLeadSearch('');
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-left hover:bg-[var(--t-surface-hover)] transition-all"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)' }}>
                      {lead.name[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--t-text)' }}>{lead.name}</p>
                      <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{lead.phone || 'No phone'}</p>
                    </div>
                  </button>
                ))}
              {leads.filter(l => !assignLeadSearch || l.name.toLowerCase().includes(assignLeadSearch.toLowerCase())).length === 0 && (
                <p className="text-sm text-center py-4" style={{ color: 'var(--t-text-muted)' }}>No leads found</p>
              )}
            </div>
            {getMeta(assignLeadModal.phone).linkedLeadId && (
              <button
                onClick={() => { updateMeta(assignLeadModal.phone, { linkedLeadId: null }); setAssignLeadModal({ isOpen: false, phone: '' }); toast.success('Lead unlinked'); }}
                className="w-full mt-3 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'var(--t-error)', color: '#fff' }}
              >Unlink Lead</button>
            )}
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="w-full max-w-xs rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--t-text)' }}>New Folder</h3>
            <input
              autoFocus
              type="text"
              placeholder="Folder name..."
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createFolder()}
              className="w-full px-3 py-2.5 rounded-xl text-sm outline-none mb-4"
              style={{ background: 'var(--t-background)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
            />
            {folders.length > 0 && (
              <div className="mb-4 space-y-1">
                {folders.map(f => (
                  <div key={f.id} className="flex items-center justify-between px-3 py-1.5 rounded-xl" style={{ background: 'var(--t-surface-hover)' }}>
                    <span className="text-xs font-medium" style={{ color: 'var(--t-text)' }}>📁 {f.name}</span>
                    <button onClick={() => deleteFolder(f.id)} className="p-1 rounded-lg" style={{ color: 'var(--t-error)' }}><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setShowNewFolderModal(false); setNewFolderName(''); }} className="flex-1 py-2.5 rounded-xl text-sm font-bold" style={{ background: 'var(--t-surface-hover)', color: 'var(--t-text-muted)' }}>Cancel</button>
              <button onClick={createFolder} disabled={!newFolderName.trim()} className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50" style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
